import { ContentCopy as CopyIcon } from "@mui/icons-material";
import {
  ListItem,
  ListItemText,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "components/common/TextField";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useLazyListUsersQuery } from "services/adminService";

/**
 * UserSelector
 * - Minimal, reusable selector for admin screens
 * - Fetches users scoped to a company via admin API
 *
 * Props:
 * - companyId: string (required)
 * - value: user object or null (expects { username, email, enabled } at minimum)
 * - onChange: (userOrNull) => void (required)
 * - label?: string (default "Select User")
 * - sx?: object (optional styles to apply to the input)
 * - showCopyId?: boolean (default false) - shows a copy button to copy the user ID
 * - filterUsers?: (user) => boolean (optional filter function to apply to user list)
 */
const UserSelector = ({
  companyId,
  value,
  onChange,
  label = "Select User",
  sx: inputSx,
  showCopyId = false,
  filterUsers = () => true,
}) => {
  const [
    triggerListUsers,
    { data: users, isFetching, isError, isUninitialized },
  ] = useLazyListUsersQuery();

  useEffect(() => {
    if (!companyId) return;
    triggerListUsers({ companyId });
  }, [companyId, triggerListUsers]);

  const { enqueueSnackbar } = useSnackbar();

  const defaultSx = { width: "fit-content", minWidth: "20em", flexShrink: 0 };
  const sx = { ...defaultSx, ...inputSx };

  const sortedUsers = useMemo(() => {
    const list = (users ?? []).filter((u) => u?.enabled).filter(filterUsers);
    return [...list].sort((a, b) => {
      const aEmail = a?.email ?? "";
      const bEmail = b?.email ?? "";
      const emailCompare = aEmail.localeCompare(bEmail);
      if (emailCompare !== 0) {
        return emailCompare;
      }
      return (a?.username ?? "").localeCompare(b?.username ?? "");
    });
  }, [users, filterUsers]);

  const handleCopyUsername = async (e) => {
    e.stopPropagation();
    if (!value?.username) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value.username);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = value.username;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        enqueueSnackbar("Failed to copy user ID", { variant: "error" });
      }
      document.body.removeChild(textArea);
    }
  };

  const renderPlaceholder = (text) => (
    <TextField
      label={text}
      variant={"outlined"}
      size={"small"}
      disabled
      sx={sx}
    />
  );

  if (!companyId) {
    return renderPlaceholder("Select company first");
  }

  if (isError) {
    return renderPlaceholder("Failed to load users");
  }

  // While uninitialized or fetching and no data yet, show a loading field
  if (
    (isUninitialized || isFetching) &&
    (users == null || users.length === 0)
  ) {
    return (
      <TextField
        label={`${label} — Loading…`}
        variant={"outlined"}
        size={"small"}
        disabled
        InputProps={{ endAdornment: <CircularProgress size={18} /> }}
        sx={sx}
      />
    );
  }

  return (
    <Autocomplete
      options={sortedUsers}
      value={value ?? null}
      onChange={(_, newValue) => onChange?.(newValue ?? null)}
      getOptionLabel={(option) => {
        return option?.email ?? option?.username ?? "<unknown>";
      }}
      filterOptions={(options, { inputValue }) => {
        const searchTerm = inputValue.toLowerCase();
        return options.filter((option) => {
          const email = (option?.email ?? "").toLowerCase();
          const username = (option?.username ?? "").toLowerCase();
          return email.includes(searchTerm) || username.includes(searchTerm);
        });
      }}
      isOptionEqualToValue={(option, val) => option?.username === val?.username}
      renderOption={(props, option) => (
        <ListItem {...props} key={option?.username}>
          <ListItemText
            primary={option?.email ?? option?.username}
            secondary={option?.username}
            sx={{ whiteSpace: "nowrap" }}
          />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={"outlined"}
          size={"small"}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                ) : null}
                {showCopyId && value?.username && (
                  <Tooltip title={"Copy user ID"}>
                    <IconButton
                      onClick={handleCopyUsername}
                      size={"small"}
                      sx={{ mr: 0.5 }}
                    >
                      <CopyIcon fontSize={"small"} />
                    </IconButton>
                  </Tooltip>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          fullWidth
        />
      )}
      loading={isFetching}
      noOptionsText={isFetching ? "Loading…" : "No users found"}
      size={"small"}
      sx={sx}
    />
  );
};

export default UserSelector;
